import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { sessionManager } from "@/utils/sessionManager";
import { getUserIP } from "@/utils/ipUtils";
import {
  checkRateLimit as utilCheckRateLimit,
  getUserAccountAge,
  NEW_USER_LIMITS,
  ESTABLISHED_USER_LIMITS,
} from "@/utils/rateLimit";

interface CreatePostData {
  content: string;
  topic_id: string;
  parent_post_id?: string | null;
}

export const useCreatePost = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePostData) => {
      const authorId = user ? user.id : sessionManager.getTempUserId();
      const author_ip = await getUserIP().catch(() => null);
      const author_user_agent = typeof navigator !== "undefined" ? navigator.userAgent : null;

      const accountAge = user ? await getUserAccountAge(user.id) : 0;
      const config = accountAge < 7 ? NEW_USER_LIMITS : ESTABLISHED_USER_LIMITS;

      const userLimit = await utilCheckRateLimit({ userId: authorId, ip: undefined, config });
      if (!userLimit.allowed) throw new Error(userLimit.reason);

      if (author_ip) {
        const ipLimit = await utilCheckRateLimit({ userId: undefined, ip: author_ip, config });
        if (!ipLimit.allowed) throw new Error(ipLimit.reason);
      }

      // Validate topic + category level
      const { data: topic, error: topicError } = await supabase
        .from("topics")
        .select("category_id, categories(level, name, requires_moderation)")
        .eq("id", data.topic_id)
        .single();

      if (topicError) throw new Error("Invalid topic");

      if (topic.categories?.level !== 2 && topic.categories?.level !== 3) {
        throw new Error(
          `Posts can only be created in discussion or age group categories. This topic is in "${topic.categories?.name}" which is for browsing only.`
        );
      }

      const { data: post, error } = await supabase
        .from("posts")
        .insert({
          content: data.content,
          topic_id: data.topic_id,
          user_id: user?.id || null,
          parent_post_id: data.parent_post_id || null,
          author_ip,
          author_user_agent,
        } as any)
        .select()
        .single();

      if (error) throw new Error(`Failed to create post: ${error.message}`);
      return post;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      queryClient.invalidateQueries({ queryKey: ["topics"] });
      queryClient.invalidateQueries({ queryKey: ["topic"] });
    },
  });
};
